// @flow

import { useState, useEffect } from 'react';
import { type IncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { type Country } from '@tectonics/prs/country';
import { type PaymentSpec } from '@tectonics/prs/payment-spec';
import { type PaymentSpecGenerator, generatePaymentSpec } from '@tectonics/prs/payment-spec/generator';
import { getLookAndFeelFromEnvironment } from './get-look-and-feel-from-environment';

async function loadCountryBasedPaymentSpecGenerator(country: Country): Promise<{ default: PaymentSpecGenerator }> {
  switch (country) {
    case 'br':
      return import('@tectonics/prs/brazil/payment-spec-generator.js');

    case 'mx':
      return import('@tectonics/prs/mexico/payment-spec-generator.js');

    case 'ar':
      return import('@tectonics/prs/argentina/payment-spec-generator.js');

    case 'co':
      return import('@tectonics/prs/colombia/payment-spec-generator.js');

    case 'cl':
      return import('@tectonics/prs/chile/payment-spec-generator.js');

    case 'pe':
      return import('@tectonics/prs/peru/payment-spec-generator.js');

    case 'bo':
      return import('@tectonics/prs/bolivia/payment-spec-generator.js');

    case 'ec':
      return import('@tectonics/prs/ecuador/payment-spec-generator.js');

    case 'uy':
      return import('@tectonics/prs/uruguay/payment-spec-generator.js');

    case 'cr':
      return import('@tectonics/prs/costa-rica/payment-spec-generator.js');

    case 'py':
      return import('@tectonics/prs/paraguay/payment-spec-generator.js');

    case 'pa':
      return import('@tectonics/prs/panama/payment-spec-generator.js');

    case 'sv':
      return import('@tectonics/prs/el-salvador/payment-spec-generator.js');

    case 'gt':
      return import('@tectonics/prs/guatemala/payment-spec-generator.js');

    case 'do':
      return import('@tectonics/prs/dominican-republic/payment-spec-generator.js');

    case 'za':
      return import('@tectonics/prs/south-africa/payment-spec-generator.js');

    case 'ke':
      return import('@tectonics/prs/kenya/payment-spec-generator.js');

    case 'ng':
      return import('@tectonics/prs/nigeria/payment-spec-generator.js');

    case 'in':
      return import('@tectonics/prs/india/payment-spec-generator.js');

    case 'ph':
      return import('@tectonics/prs/philippines/payment-spec-generator.js');

    case 'tz':
      return import('@tectonics/prs/tanzania/payment-spec-generator.js');

    case 'ug':
      return import('@tectonics/prs/uganda/payment-spec-generator.js');

    case 'zm':
      return import('@tectonics/prs/zambia/payment-spec-generator.js');

    case 'eg':
      return import('@tectonics/prs/egypt/payment-spec-generator.js');

    case 'gh':
      return import('@tectonics/prs/ghana/payment-spec-generator.js');

    case 'id':
      return import('@tectonics/prs/indonesia/payment-spec-generator.js');

    case 'jm':
      return import('@tectonics/prs/jamaica/payment-spec-generator.js');

    case 'th':
      return import('@tectonics/prs/thailand/payment-spec-generator.js');

    case 'tr':
      return import('@tectonics/prs/turkey/payment-spec-generator.js');

    default:
      throw new Error(`Dynamic Payment Spec Generator - There is no support for country "${country}".`);
  }
}

export function useDynamicPaymentSpec(incomingPayment: ?IncomingPayment, localTenancy: ?string): [?PaymentSpec,?Error] {
  const [spec, setSpec] = useState <?PaymentSpec> (null);
  const [loadingSpecError, setLoadingSpecError] = useState <?Error> (null);

  useEffect(() => {
    (async () => {
      if (!incomingPayment) return;

      setLoadingSpecError(null);

      try {
        const generatorModule = await loadCountryBasedPaymentSpecGenerator(getCountry(incomingPayment));
        if (!generatorModule || !generatorModule.default) {
          throw new Error('Dynamic Payment Spec Generator - module failed to load');
        }
        const spec = generatePaymentSpec({
          incomingPayment,
          localTenancy: localTenancy || 'CROSS_BORDER',
          generator: generatorModule.default,
          lookAndFeelCode: getLookAndFeelFromEnvironment(incomingPayment).code,
        });
        setSpec(spec);
      } catch (e) {
        setLoadingSpecError(e);
        setSpec(null);
      }
    })();
  }, [incomingPayment]);

  return [spec, loadingSpecError];
}
